/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, forwardRef, useMemo } from 'react';
import { Box, Flex, useToast, Text, Button, Tabs, TabList, TabPanel, TabPanels, Tab, useDisclosure, Badge, SimpleGrid, Divider } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery } from '@apollo/client';
import { faFileAlt, faUserEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserDislayNameById from '../../../../components/UserDislayNameById';
import c from '../../../../constant';
import useStore from '../../../../store';
import { isAbleAllowEditing, isDataViewer } from '../../../../utils/market-development';
import CompanyName from '../../../CompanyName';
import LabelDate from '../../../LabelDate';
import { mdMonthlyDueDate } from '../../../../utils/mdReportDueDate';
import moment from 'moment';
import Loading from '../../../Loading';
import EditMonthlyReportModal from '../../../Modal/EditMonthlyReportModal';
import { CompanyBadge } from '../../../Other';
import SpreadSheetKeyValuePair from '../../Miscellaneous/spreadsheet-key-value-pair';
import ExcelLife from '../Excel/ExcelLife';
import SpreadSheetTable from './table';
import EditHistoryModal from '../../Miscellaneous/edit-history-modal';
import useMoment from '../../../../hooks/useMoment';

const Q_DISTRIBUTION_ITEMS = gql`
	query get($id: String!) {
		getMonthlyDistributionReportItems(id: $id) {
			id
			reportType
			month
			year
			createdAt
			updatedAt
			company {
				id
				name
				nameKhmer
				logo
				type
			}
			isEditing
			editingUpdatedAt
			editingUpdatedBy
			editingDueDate
			editingDescription
			penalties {
				id
				active
			}
			distributionItems {
				id
				productLine {
					id
					nameKh
					nameEn
					type
				}
			}
		}
	}
`;

const Page = forwardRef(({ id = null }, ref) => {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { toDateString, toMonth, toYear } = useMoment();
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [month, setMonth] = useState(null);
	const [year, setYear] = useState(null);
	const [isEditing, setEditing] = useState(false);
	const [createdAt, setCreatedAt] = useState(null);
	const [updatedAt, setUpdatedAt] = useState(null);
	const [isPenalized, setPenalized] = useState(false);
	const [company, setCompany] = useState(null);
	const [distributionItems, setDistributionItems] = useState(null);
	const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
	const [getDetail, { data, loading, error }] = useLazyQuery(Q_DISTRIBUTION_ITEMS, { fetchPolicy: 'no-cache' });

	useEffect(loadDetail, [id]);

	useEffect(() => {
		if (data) {
			setMonth(parseInt(data?.getMonthlyDistributionReportItems?.month));
			setYear(parseInt(data?.getMonthlyDistributionReportItems?.year));
			setEditing(data?.getMonthlyDistributionReportItems?.isEditing);
			setCreatedAt(data?.getMonthlyDistributionReportItems?.createdAt);
			setUpdatedAt(data?.getMonthlyDistributionReportItems?.updatedAt);
			setPenalized(data?.getMonthlyDistributionReportItems?.penalties?.length > 0);
			setCompany(data?.getMonthlyDistributionReportItems?.company);
			setDistributionItems(data?.getMonthlyDistributionReportItems?.distributionItems);
		}
	}, [data]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function loadDetail() {
		id && getDetail({ variables: { id } });
	}

	const dueDate = useMemo(() => mdMonthlyDueDate({ year, month }), [year, month]);
	const isDueDateReached = useMemo(() => (dueDate ? moment().isSameOrAfter(dueDate, 'second') : false), [dueDate]);
	const canViewData = useMemo(() => isDataViewer({ user, companyType: 'Life' }), [user]);
	const canAllowEditing = useMemo(() => canViewData && isAbleAllowEditing({ user, companyType: 'Life' }), [user]);

	return (
		<>
			{loading ? (
				<>
					<Loading />
				</>
			) : (
				<>
					<Flex pl={4} pr={4}>
						<SimpleGrid columns={[1, null, 2]} w="100%" spacingX={4} spacingY={4}>
							<Flex w="100%" direction="column" border="1px dotted #dbdbdb" borderRadius={c.borderRadius} pl={4} pr={4} pt={4} pb={4}>
								<>
									{isPenalized && (
										<Box>
											<Badge colorScheme="red" variant="solid">
												{t('Penalized')}
											</Badge>
										</Box>
									)}
								</>
								<SpreadSheetKeyValuePair label="Company" value={<CompanyName company={company} />} />
								<SpreadSheetKeyValuePair label="Type" value={<CompanyBadge type={company?.type} />} />
								<SpreadSheetKeyValuePair label="Month" value={toMonth(month)} />
								<SpreadSheetKeyValuePair label="Year" value={toYear(year)} />
								<SpreadSheetKeyValuePair label="Submitted At" value={<LabelDate input={createdAt} outputFormat={c.dateTimeFormat} />} />
								<>
									{updatedAt && (
										<>
											<SpreadSheetKeyValuePair
												label="Updated at"
												value={<LabelDate input={updatedAt} outputFormat={c.dateTimeFormat} />}
											/>
										</>
									)}
								</>
								{isDueDateReached && canViewData && isEditing && (
									<>
										<Divider variant="dashed" mt={2} mb={2} />
										<SpreadSheetKeyValuePair
											label="Allow Editing"
											value={
												<LabelDate input={data?.getMonthlyDistributionReportItems?.editingUpdatedAt} outputFormat={c.dateTimeFormat} />
											}
										/>
										<SpreadSheetKeyValuePair
											label="By"
											value={<UserDislayNameById id={data?.getMonthlyDistributionReportItems?.editingUpdatedBy} />}
										/>
										<SpreadSheetKeyValuePair
											label="Due Date"
											value={
												<LabelDate input={data?.getMonthlyDistributionReportItems?.editingDueDate} outputFormat={c.dateTimeFormat} />
											}
										/>
										<SpreadSheetKeyValuePair label="Description" value={data?.getMonthlyDistributionReportItems?.editingDescription} />
										{canAllowEditing && (
											<>
												<Box mt={2}>
													<Button
														onMouseDown={onOpen}
														isLoading={false}
														isDisabled={false}
														onClick={() => null}
														leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
														colorScheme="red"
														variant="solid"
														size="sm"
														borderRadius={c.borderRadius}
														border="1px solid #bdc3c7"
														mr={2}
														width={lang === 'kh' ? '219px' : '195px'}
													>
														{t('Stop Allowing Editing')}
													</Button>
												</Box>
											</>
										)}
									</>
								)}
								<Flex mt={2}>
									{isDueDateReached && canAllowEditing && !isEditing && (
										<Button
											onMouseDown={onOpen}
											isLoading={false}
											isDisabled={false}
											onClick={() => null}
											leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											mr={2}
										>
											{t('Allow Editing')}
										</Button>
									)}
									{canViewData && <ExcelLife id={id} month={month} year={year} numColumns={23} />}
									{canViewData && (
										<Flex ml={2}>
											<Button
												colorScheme="gray"
												variant="solid"
												size="sm"
												borderRadius={c.borderRadius}
												border="1px solid #bdc3c7"
												leftIcon={<FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 16 }} />}
												onClick={onHistoryOpen}
											>
												{t('View Logs')}
											</Button>
										</Flex>
									)}
								</Flex>
								{!isDueDateReached && canViewData && dueDate && (
									<Box h="100%" mt={2}>
										<Text fontSize="15px" fontStyle="italic" fontWeight="bold" justifyContent="center" color="orange.500">
											{t('Report can still be edited until the due date:')}&nbsp;
											{toDateString(dueDate, c.dateTimeFormat)}
										</Text>
									</Box>
								)}
							</Flex>
						</SimpleGrid>
					</Flex>
					<Box pl={4} pr={4} pb={4} pt={4} overflow="clip" hidden={canViewData ? false : true}>
						<Tabs isLazy variant="soft-rounded" colorScheme="green">
							<TabList overflow="scroll">
								{distributionItems?.map((el) => {
									return (
										<Tab key={el?.id}>
											<Text fontSize="small">{el?.productLine?.nameKh}</Text>
										</Tab>
									);
								})}
							</TabList>
							<TabPanels>
								{distributionItems?.map((el) => {
									return (
										<TabPanel key={el?.id} pl={0} pr={0} pb={0}>
											<>
												<SpreadSheetTable distributionItemId={el?.id} />
											</>
										</TabPanel>
									);
								})}
							</TabPanels>
						</Tabs>
					</Box>
				</>
			)}
			{isOpen && <EditMonthlyReportModal id={id} type="Distribution" isEditable={isEditing} onClose={onClose} onCallback={loadDetail} />}
			{isHistoryOpen && <EditHistoryModal id={id} period="Monthly" category="Distribution" isOpen={isHistoryOpen} onClose={onHistoryClose} />}
		</>
	);
});

export default Page;
